import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useMobile } from './hooks/useMobile';
import { Skeleton, Spinner } from './components/ui';

// Layout — always loaded (app shell)
import { MainLayout } from './components/layout/MainLayout';
// Full-screen brand loader for route transitions (login → dashboard, etc.)
import CockpitLoaderOverlay from './components/cockpit-loader/CockpitLoaderOverlay';
// Chromeless splash shown in the new checkout tab while the Stripe session is
// created. Direct (non-lazy) import — CockpitLoader is already in the main
// bundle, and this avoids a Suspense-skeleton flash on the splash.
import { CheckoutRedirectingPage } from './features/pricing/pages/CheckoutRedirectingPage';
// Admin-only floating pill to preview the accountant workspace.
import { AdminViewToggle } from './components/AdminViewToggle';

/**
 * `/register` is preserved for backward compatibility (old emails, marketing
 * artifacts, external bookmarks linking to `/register?plan=pro&…`). Query
 * params are preserved and forwarded to the new `/signup` route, so a
 * pre-selection like `?plan=pro` can be honoured by the wizard.
 */
function RegisterAlias() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/signup', search }} replace />;
}

// Lazy-loaded pages
const LoginPage = lazy(() => import('./features/auth').then(m => ({ default: m.LoginPage })));
const ForgotPasswordPage = lazy(() => import('./features/auth').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./features/auth').then(m => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => import('./features/auth').then(m => ({ default: m.VerifyEmailPage })));
const CheckEmailPage = lazy(() => import('./features/auth').then(m => ({ default: m.CheckEmailPage })));
const SignupWizardPage = lazy(() => import('./features/signup').then(m => ({ default: m.SignupWizardPage })));
const WelcomePage = lazy(() => import('./features/onboarding').then(m => ({ default: m.WelcomePage })));
const ProfileCompletePage = lazy(() => import('./features/onboarding').then(m => ({ default: m.ProfileCompletePage })));
const TermsPage = lazy(() => import('./features/legal').then(m => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./features/legal').then(m => ({ default: m.PrivacyPage })));
const DashboardPage = lazy(() => import('./features/dashboard/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const DealsPage = lazy(() => import('./features/deals/pages/DealsPage').then(m => ({ default: m.DealsPage })));
const DealDetailPage = lazy(() => import('./features/deals/pages/DealDetailPage').then(m => ({ default: m.DealDetailPage })));
const DealAnalysisPage = lazy(() => import('./features/deals/pages/DealAnalysisPage').then(m => ({ default: m.DealAnalysisPage })));
const UploadPage = lazy(() => import('./features/upload/pages/UploadPage').then(m => ({ default: m.UploadPage })));
const ExtractionPage = lazy(() => import('./features/extraction/pages/ExtractionPage').then(m => ({ default: m.ExtractionPage })));
const AnalysisPage = lazy(() => import('./features/analysis/pages/AnalysisPage').then(m => ({ default: m.AnalysisPage })));
const ComparePage = lazy(() => import('./features/comparison/pages/ComparePage').then(m => ({ default: m.ComparePage })));
const DocumentsPage = lazy(() => import('./features/documents/pages/DocumentsPage').then(m => ({ default: m.DocumentsPage })));
const AdminPage = lazy(() => import('./features/admin/pages/AdminPage').then(m => ({ default: m.AdminPage })));
const BanksPage = lazy(() => import('./features/admin/pages/BanksPage').then(m => ({ default: m.BanksPage })));
const ExtractionInspectorDetail = lazy(() => import('./features/admin/pages/ExtractionInspectorDetail').then(m => ({ default: m.ExtractionInspectorDetail })));
const SettingsPage = lazy(() => import('./features/settings').then(m => ({ default: m.SettingsPage })));
const PaymentSuccessPage = lazy(() => import('./features/pricing').then(m => ({ default: m.PaymentSuccessPage })));
const NegotiationPage = lazy(() => import('./features/negotiation').then(m => ({ default: m.NegotiationPage })));
const ValidationSummaryPage = lazy(() => import('./features/extraction/pages/ValidationSummaryPage').then(m => ({ default: m.ValidationSummaryPage })));
const ChatPage = lazy(() => import('./features/chat/pages/ChatPage').then(m => ({ default: m.ChatPage })));

// Dev-only gallery of the Structure card across all its render states. The route
// is registered only under import.meta.env.DEV (below), so this lazy chunk is
// never loaded in production.
const StructureStatesPage = lazy(() => import('./features/dev/pages/StructureStatesPage').then(m => ({ default: m.StructureStatesPage })));

// Accountant workspace (HANDOFF_ACCOUNTANT_DASHBOARD_PHASE_A) — full-screen alt
// to MainLayout; gated to signup_segment === 'accountant'.
const AccountantLayout = lazy(() => import('./features/accountant/components/layout/AccountantLayout').then(m => ({ default: m.AccountantLayout })));
const AccountantPortfolioPage = lazy(() => import('./features/accountant/pages/AccountantPortfolioPage').then(m => ({ default: m.AccountantPortfolioPage })));
const AccountantUploadPage = lazy(() => import('./features/accountant/pages/AccountantUploadPage').then(m => ({ default: m.AccountantUploadPage })));
const AccountantDealPage = lazy(() => import('./features/accountant/pages/AccountantDealPage').then(m => ({ default: m.AccountantDealPage })));
const AccountantClientPage = lazy(() => import('./features/accountant/pages/AccountantClientPage').then(m => ({ default: m.AccountantClientPage })));
const AccountantClientsIndexPage = lazy(() => import('./features/accountant/pages/AccountantClientsIndexPage').then(m => ({ default: m.AccountantClientsIndexPage })));
const AccountantImportPage = lazy(() => import('./features/accountant/pages/AccountantImportPage').then(m => ({ default: m.AccountantImportPage })));

function PageLoader() {
  return (
    <div className="p-6 space-y-4">
      <Skeleton variant="text" />
      <Skeleton variant="card" />
      <Skeleton variant="card" />
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface-main">
        <Spinner size="lg" showLabel className="text-orange" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * SignupCompleteRoute — secondary gate that sits inside ProtectedRoute
 * and blocks access to the authenticated app surface for users who
 * created an account but abandoned the wizard before picking a persona
 * (the Step 2 segmentation choice). Without this gate, hitting browser
 * back from Step 2 or deep-linking to /dashboard bypasses signup
 * entirely — the user lands on the dashboard with no signup_segment,
 * no company profile, and no plan selection.
 *
 * Gate criterion: `user.signup_segment` is null/undefined. This is the
 * single most important onboarding field because it drives product
 * routing (SME vs Accountant features, tier pricing, GTM tracking).
 * Later wizard steps (profile, plan) are advisory — if a user skips
 * them, they can still use the app and fill in details later via
 * Settings. Persona is not optional.
 *
 * Redirect target: /signup?step=2 with replace:true so the blocked
 * route doesn't pollute history. The wizard's own ?step= param
 * handler (SignupWizardPage) picks this up and renders Step 2 on mount.
 */
function SignupCompleteRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);

  // If the user record isn't hydrated yet, let ProtectedRoute's loading
  // screen continue to cover this. Without this guard, a first paint
  // with user=null would redirect mid-auth-check.
  if (user && !user.signup_segment) {
    return <Navigate to="/signup?step=2" replace />;
  }

  return <>{children}</>;
}

/**
 * EmailVerifiedRoute — blocks access to the authenticated app surface
 * for users who haven't verified their email address yet.
 *
 * Sits inside ProtectedRoute + SignupCompleteRoute. If email_verified
 * is false, the user is redirected to /welcome which shows ONLY the
 * verification prompt (no demo, no upload). Once they verify, auto-poll
 * on /welcome detects the change and lets them through.
 */
function EmailVerifiedRoute({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);

  // Let ProtectedRoute's loading screen handle the un-hydrated case.
  if (user && user.email_verified === false) {
    return <Navigate to="/welcome" replace />;
  }

  return <>{children}</>;
}


/**
 * HANDOFF_ACCOUNTANT_DASHBOARD_PHASE_G — mobile path allowlist.
 *
 * The previous exact-match `MOBILE_ALLOWED_PATHS.includes(pathname)` caused
 * an infinite redirect loop for mobile accountants:
 *   /  → AccountantRedirect → /portal
 *   /portal → MobileChatGate → /chat  (didn't match exact list)
 *   /chat  → SMEGate → /portal  (accountant blocked from SME chat)
 *   …loop.
 *
 * Phase G replaces it with a prefix-allowlist + blocked-subpath model:
 *   - MOBILE_ALLOWED_PREFIXES: pathname must equal or be a `/`-segment
 *     descendant of one of these. So /portal/clients/3 matches /portal,
 *     but /portal-other does NOT (the trailing-slash test prevents it).
 *   - MOBILE_BLOCKED_SUBPATHS: explicit sub-routes that remain
 *     desktop-only even though their parent prefix is allowed.
 *
 * Accountants: /portal (list) and /portal/clients/:id are allowed.
 *   /portal/upload and /portal/deals/:id are desktop-only.
 * SME users: /chat and /settings only (unchanged).
 *
 * Fallback redirect picks by user segment so accountants never bounce
 * into the SMEGate loop: accountant → /portal, everyone else → /chat.
 */
const MOBILE_ALLOWED_PREFIXES = ['/chat', '/settings', '/portal'];
const MOBILE_BLOCKED_SUBPATHS = ['/portal/upload', '/portal/deals', '/portal/import'];

function isMobileAllowed(pathname: string): boolean {
  if (MOBILE_BLOCKED_SUBPATHS.some(p => pathname === p || pathname.startsWith(p + '/'))) {
    return false;
  }
  return MOBILE_ALLOWED_PREFIXES.some(
    p => pathname === p || pathname.startsWith(p + '/'),
  );
}

function MobileChatGate({ children }: { children: React.ReactNode }) {
  const isMobile = useMobile();
  const { pathname } = useLocation();
  const user = useAuthStore(s => s.user);

  if (isMobile && !isMobileAllowed(pathname)) {
    const fallback = user?.signup_segment === 'accountant' ? '/portal' : '/chat';
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}

/**
 * SMEGate — blocks accountant-segment users from the SME-themed
 * MainLayout surface. AccountantRedirect already covers the "/" index,
 * but every named SME route (/dashboard, /deals, /upload, /compare, /chat,
 * …) had no segment check, so accountants could deep-link or type URLs
 * straight into the SME workspace. This gate closes that leak.
 *
 * ADMIN bypass: admins skip the gate when NOT previewing. When the
 * AdminViewToggle has set previewSegment === 'accountant', admins are
 * bounced too — preview is meant to be a faithful accountant experience,
 * so URL-navigation back to /dashboard is disabled until the admin
 * exits preview via the floating pill.
 */
function SMEGate({ children }: { children: React.ReactNode }) {
  const user = useAuthStore(s => s.user);
  const previewSegment = useAuthStore(s => s.previewSegment);
  const isAccountant = user?.signup_segment === 'accountant' && user?.user_role !== 'ADMIN';
  const isAdminInPreview = user?.user_role === 'ADMIN' && previewSegment === 'accountant';
  if (isAccountant || isAdminInPreview) {
    return <Navigate to="/portal" replace />;
  }
  return <>{children}</>;
}

/**
 * AccountantRedirect — forks the root "/" index route by signup_segment.
 * Accountants land on /portal; everyone else continues to /dashboard.
 * Used as the index route inside the MainLayout block.
 */
function AccountantRedirect() {
  const user = useAuthStore(s => s.user);
  if (user?.signup_segment === 'accountant') {
    return <Navigate to="/portal" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

export default function App() {
  return (
    <>
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupWizardPage />} />
      <Route path="/signup/:step" element={<SignupWizardPage />} />
      <Route path="/register" element={<RegisterAlias />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/check-email" element={<CheckEmailPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Checkout redirect splash — chromeless full-screen loader shown in the
          new tab while the Stripe Checkout session is created (see checkoutStore). */}
      <Route path="/checkout/redirecting" element={<CheckoutRedirectingPage />} />

      {/* Dev-only — Structure card states gallery. Stripped from production:
          import.meta.env.DEV is statically false there, so the <Route> is never
          registered and its lazy chunk is never requested. */}
      {import.meta.env.DEV && <Route path="/dev/structure-states" element={<StructureStatesPage />} />}

      {/* Onboarding routes (protected but outside MainLayout) */}
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <WelcomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/complete"
        element={
          <ProtectedRoute>
            <EmailVerifiedRoute>
              <ProfileCompletePage />
            </EmailVerifiedRoute>
          </ProtectedRoute>
        }
      />

      {/* Protected routes — also gated on signup_segment being set, so users
          who abandon the wizard after Step 1 (account created) but before
          Step 2 (persona picked) get bounced back into the wizard instead
          of landing on an empty, un-segmented dashboard. */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <SignupCompleteRoute>
              <EmailVerifiedRoute>
                <MobileChatGate>
                  <SMEGate>
                    <MainLayout />
                  </SMEGate>
                </MobileChatGate>
              </EmailVerifiedRoute>
            </SignupCompleteRoute>
          </ProtectedRoute>
        }
      >
        <Route index element={<AccountantRedirect />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="deals" element={<DealsPage />} />
        <Route path="deals/:dealId" element={<DealDetailPage />} />
        <Route path="deals/:dealId/upload" element={<UploadPage />} />
        <Route path="deals/:dealId/analysis" element={<DealAnalysisPage />} />
        <Route path="upload" element={<UploadPage />} />
        <Route path="term-sheets/:termSheetId/extraction" element={<ExtractionPage />} />
        {/* Verification removed per §7.5 — redirect old URLs to analysis */}
        <Route path="term-sheets/:termSheetId/verification" element={<Navigate to="../analysis" replace />} />
        {/* The AnalysisPage (5-component grid, covenant table, cost
            breakdown, negotiation paywall) is the detailed post-extraction
            surface. Reachable for real term sheets and — via the `demo`
            route — a marketing-friendly fixture walkthrough. */}
        <Route path="term-sheets/:termSheetId/analysis" element={<AnalysisPage />} />
        <Route path="analysis/demo" element={<AnalysisPage />} />
        <Route path="deals/:dealId/compare" element={<ComparePage />} />
        <Route path="compare" element={<ComparePage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="admin" element={<AdminPage />} />
        <Route path="admin/banks" element={<BanksPage />} />
        <Route path="admin/extraction-inspector/:id" element={<ExtractionInspectorDetail />} />
        {/* Settings — single unified dashboard. The tabbed sub-routes
            were consolidated back into one page; legacy URLs redirect
            to /settings so existing bookmarks (and the post-checkout
            redirect from PaymentSuccessPage) keep working. */}
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/profile" element={<Navigate to="/settings" replace />} />
        <Route path="settings/billing" element={<Navigate to="/settings" replace />} />
        <Route path="settings/security" element={<Navigate to="/settings" replace />} />
        <Route path="settings/privacy" element={<Navigate to="/settings" replace />} />
        <Route path="settings/legacy" element={<Navigate to="/settings" replace />} />
        <Route path="pricing" element={<Navigate to="/dashboard" replace />} />
        <Route path="checkout/success" element={<PaymentSuccessPage />} />
        <Route path="deals/:dealId/negotiations" element={<NegotiationPage />} />
        <Route path="term-sheets/:termSheetId/summary" element={<ValidationSummaryPage />} />
      </Route>

      {/* Partner portal — full-screen accountant/advisor surface, own layout,
          no MainLayout. URL surface renamed from /accountant → /portal
          (2026-05-21). AccountantLayout enforces signup_segment==='accountant'
          (with ADMIN preview bypass) and redirects SME users to /dashboard. */}
      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <SignupCompleteRoute>
              <EmailVerifiedRoute>
                <AccountantLayout />
              </EmailVerifiedRoute>
            </SignupCompleteRoute>
          </ProtectedRoute>
        }
      >
        <Route index element={<AccountantPortfolioPage />} />
        <Route path="upload" element={<AccountantUploadPage />} />
        <Route path="deals/:termSheetId" element={<AccountantDealPage />} />
        {/* Phase I G2 — `/portal/clients` (no segment) renders an empty-state
            landing instead of silently redirecting to /dashboard. */}
        <Route path="clients" element={<AccountantClientsIndexPage />} />
        <Route path="clients/:clientId" element={<AccountantClientPage />} />
        <Route path="import" element={<AccountantImportPage />} />
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
    {/* Mounted once at root — visibility controlled by useTransitionLoader. */}
    <CockpitLoaderOverlay />
    <AdminViewToggle />
    </>
  );
}